import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DetectionId } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { IconButton, RoundedInput, Row, Select, Subheader, Snackbar, AppText, AppIcon, Tooltip, useTheme, useSnackbar, } from '@streem/ui-react';
import { useEffect, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, skip, takeUntil } from 'rxjs/operators';
import { streem } from 'streem-sdk-protobuf';
import { useObservable } from '../../../../hooks/rxjs';
import { DetectionInputContainer, FeedbackHeaderContainer, ModelSerialWrapper, } from './model_serial_list_styles';
export const ModelSerialFeedback = ({ modelSerialDetections, onAdd, onEdit, onDelete, isTrayOpen }) => {
    const theme = useTheme();
    const [inProgressDetections, setInProgressDetections] = useState([]);
    const removeInProgress = (detectionId) => {
        const newInProgress = inProgressDetections.filter(id => !id.compare(detectionId));
        setInProgressDetections(newInProgress);
    };
    const { addNotification, snackbarNotifications, scheduleNotificationForRemoval } = useSnackbar();
    const createNotification = (id) => {
        addNotification({
            id,
            message: 'Copied to clipboard!',
            icon: _jsx(AppIcon, { color: theme.colors.green50, name: "DoneIcon" }),
        });
        scheduleNotificationForRemoval(id, 2000);
    };
    const handleCopyToClipboard = (description, id) => {
        navigator.clipboard.writeText(description ?? '');
        createNotification(id ?? '');
    };
    return (_jsxs(ModelSerialWrapper, { "data-testid": "model-serial-section", children: [_jsxs(FeedbackHeaderContainer, { children: [_jsx(Subheader, { size: "mediumLarge", "data-testid": "model-serial-heading", children: "Model and Serial Numbers" }), _jsx(IconButton, { iconName: "AddIcon", label: "Add a detection", onClick: () => setInProgressDetections([...inProgressDetections, new DetectionId()]) })] }), inProgressDetections.map(id => (_jsx(Feedback, { onEdit: newDetection => {
                    onAdd(newDetection?.description ?? undefined, newDetection?.type ?? undefined);
                    removeInProgress(id);
                }, onDelete: () => removeInProgress(id) }, id.toString()))), modelSerialDetections.map(det => {
                const detectionId = det.detectionId || undefined; // remove null as a possibility to match DetectionId type
                return (_jsx(Feedback, { detection: det, onEdit: newDetection => onEdit(new DetectionId(detectionId), newDetection), onDelete: () => onDelete(new DetectionId(detectionId)), onCopy: () => {
                        handleCopyToClipboard(det?.description, det.detectionId);
                    }, onSearch: () => {
                        const searchQuery = encodeURIComponent(det?.description ?? '');
                        window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
                    } }, det.detectionId));
            }), _jsx(Snackbar, { content: snackbarNotifications, position: { vertical: 'top', horizontal: 'center' }, isTrayOpen: isTrayOpen })] }));
};
const detectionTypes = [
    {
        label: 'Model Number',
        value: streem.api.Detection.Type.DETECTION_TYPE_MODEL,
    },
    {
        label: 'Serial Number',
        value: streem.api.Detection.Type.DETECTION_TYPE_SERIAL,
    },
];
const Feedback = ({ detection: originalDetection, onEdit = () => { }, onDelete = () => { }, onCopy = () => { }, onSearch = () => { }, }) => {
    const [deleting, setDeleting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [saveRx] = useState(new BehaviorSubject(originalDetection));
    const [detection] = useObservable(saveRx);
    const theme = useTheme();
    useEffect(() => {
        const complete = new Subject();
        saveRx
            .pipe(takeUntil(complete), skip(1), // skip first (replay)
        debounceTime(1000), filter(x => Boolean(x?.type)))
            .subscribe({
            next: newDetection => {
                setSubmitting(true);
                onEdit(newDetection);
            },
        });
        return () => complete.next(true);
    }, [onEdit, saveRx, setSubmitting]);
    useEffect(() => {
        setSubmitting(false);
    }, [originalDetection?.type, originalDetection?.description]);
    if (deleting) {
        return null;
    }
    const initialValue = detectionTypes.find(detection => detection.value === originalDetection?.type);
    return (_jsx(_Fragment, { children: _jsxs(DetectionInputContainer, { children: [_jsx("div", { style: { marginLeft: '-12px' }, children: _jsx(Select, { isDisabled: submitting, options: detectionTypes, initialValue: initialValue, placeholder: "Select Type", onSelect: newType => {
                            // changing type should immediately update
                            setDeleting(true);
                            onEdit({ ...detection, type: newType.value });
                        }, styles: {
                            container: base => ({
                                ...base,
                                width: '45%',
                            }),
                            control: base => ({
                                ...base,
                                border: 'none',
                                boxShadow: 'none',
                            }),
                            placeholder: base => ({
                                ...base,
                                '& > span': {
                                    color: theme.colors.grey90,
                                },
                            }),
                            option: (base, { isSelected, isFocused }) => ({
                                ...base,
                                backgroundColor: isSelected
                                    ? theme.colors.grey05
                                    : isFocused
                                        ? theme.colors.grey10
                                        : undefined,
                            }),
                        } }) }), _jsxs(Row, { ml: '-6px', children: [_jsx(RoundedInput, { disabled: !detection?.type, name: detection?.detectionId ?? '', value: detection?.description ?? '', placeholder: detectionTypes.find(x => x.value === detection?.type)?.label ??
                                'Model/Serial Number', style: { height: 'auto' }, onChange: evt => {
                                invariant(detection, 'Cannot find a detection to change');
                                saveRx.next({ ...detection, description: evt.target.value });
                            }, "data-testid": detection?.detectionId ?? 'detection-id-input' }), _jsx(IconButton, { disabled: submitting || !detection?.description, iconName: "CopyIcon", label: "Copy this detection to clipboard", onClick: onCopy }), _jsx(Tooltip, { showTooltip: showTooltip, message: _jsx(AppText, { size: "small", children: "Search the web" }), placement: "top", color: "white", children: _jsx(IconButton, { onMouseEnter: () => setShowTooltip(true), onMouseLeave: () => setShowTooltip(false), disabled: submitting || !detection?.description, iconName: "MagnifyingGlassIcon", label: "Search this detection online", onClick: onSearch }) }), _jsx(IconButton, { disabled: submitting, iconName: "TrashIcon", hoverFill: "red", label: "Delete this detection", onClick: () => {
                                setSubmitting(true);
                                onDelete();
                            } })] })] }) }));
};
//# sourceMappingURL=model_serial_feedback.js.map